import React, { useCallback, useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import moment from 'moment';
import { Button, Checkbox, ListItemText, MenuItem, TextField, Typography } from '@mui/material';
import MeetingService from 'services/MeetingService';
import MeetingDateTimeFormItem from './MeetingDateTimeFormItem';
import Modal from 'components/shared/Modal';
import GridContainer from 'components/shared/GridContainer';
import GridItem from 'components/shared/GridItem';
import PasswordFormItem from 'components/shared/PasswordFormItem';
import { ModalProps } from 'store/types/ComponentProps';
import Meeting, { MeetingView } from 'store/types/Meeting';
import committees from 'store/configs/Committees';
import regions from 'store/configs/Regions';
import StaticSelectOption from 'store/types/StaticSelectOption';
import { defaultFormProps, defaultInputProps, getRequiredValidationRule, getValidationErrorProps } from 'util/form';
import { defaultSnackbarErrorProps } from 'util/layout';

interface EditMeetingModalProps extends ModalProps {
  onSubmit: () => void;
  data?: MeetingView;
}

interface EditMeetingFormValues extends Meeting {
  confirmPassword: string;
}

const defaultValues: EditMeetingFormValues = {
  id: '',
  title: '',
  committees: [],
  region: '',
  password: '',
  confirmPassword: '',
  startDate: null,
  endDate: null,
  timezone: '',
};

const MEETING_DATETIME_FORMAT = 'YYYY-MM-DD[T]HH:mm:ss';

const EditMeetingModal: React.FunctionComponent<EditMeetingModalProps> = ({ onClose, open, onSubmit, data }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState<boolean>(false);
  const EditMeetingForm = useForm<EditMeetingFormValues>({
    ...defaultFormProps,
    defaultValues,
  });
  const { formState, control, handleSubmit, reset, watch, setValue } = EditMeetingForm;
  const password = watch('password');
  const startDateValue = watch('startDate');
  const endDateValue = watch('endDate');

  const submitButtonDisabled = !formState.isValid || !formState.isDirty || loading;

  useEffect(() => {
    reset({
      id: data?.id || '',
      title: data?.title || '',
      committees: data?.committees || [],
      region: data?.region || '',
      password: '',
      confirmPassword: '',
      startDate: data?.startDate ? moment(data.startDate).format(MEETING_DATETIME_FORMAT) : null,
      endDate: data?.endDate ? moment(data.endDate).format(MEETING_DATETIME_FORMAT) : null,
      timezone: data?.timezone || '',
    });
  }, [data, reset]);

  useEffect(() => {
    if (!startDateValue) return;

    if (!data && (!endDateValue || moment(endDateValue).isBefore(moment(startDateValue)))) {
      setValue(
        'endDate',
        moment(startDateValue, MEETING_DATETIME_FORMAT, true).add(2, 'hours').format(MEETING_DATETIME_FORMAT),
        { shouldDirty: true }
      );
    }
  }, [startDateValue, endDateValue, data, setValue]);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const handleMeetingSubmit = useCallback(
    (formValues: Meeting) => {
      console.log('SUBMIT formValues:', formValues);
      setLoading(true);
      if (data && data.id) {
        MeetingService.updateMeeting(formValues)
          .then((result) => {
            reset();
            onSubmit();
            enqueueSnackbar(`Updated ${result.title}`, { variant: 'success' });
          })
          .catch((error: string) => {
            enqueueSnackbar(`Failed to update ${formValues.title}`, defaultSnackbarErrorProps);
            console.error('Error updating meeting:', error);
          })
          .finally(() => setLoading(false));
        return;
      } else {
        MeetingService.createMeeting(formValues)
          .then((result) => {
            reset();
            onSubmit();
            enqueueSnackbar(`Created ${result.title}`, { variant: 'success' });
          })
          .catch((error: string) => {
            enqueueSnackbar(`Failed to create ${formValues.title}`, defaultSnackbarErrorProps);
            console.error('Error creating meeting:', error);
          })
          .finally(() => setLoading(false));
      }
    },
    [data, enqueueSnackbar, onSubmit, reset]
  );

  const validatePassword = useCallback(
    (value: string) => (password === value ? true : 'Passwords do not match'),
    [password]
  );

  return (
    <Modal
      title={data ? 'Update Meeting' : 'Create Meeting'}
      open={open}
      maxWidth={'sm'}
      loading={loading}
      onClose={handleClose}
      onSubmit={handleSubmit(handleMeetingSubmit)}
      actions={
        <>
          <Button color={'secondary'} variant={'outlined'} disabled={loading} onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" color={'primary'} variant={'contained'} disabled={submitButtonDisabled}>
            Save
          </Button>
        </>
      }
    >
      <FormProvider {...EditMeetingForm}>
        <GridContainer>
          <GridItem>
            <Controller
              render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
                <TextField
                  {...defaultInputProps}
                  {...getValidationErrorProps(error)}
                  inputRef={ref}
                  onChange={onChange}
                  value={value}
                  label={'Committees'}
                  required
                  select
                  SelectProps={{
                    multiple: true,
                    renderValue: (selected) => {
                      return (selected as string[])
                        ?.map((value) => committees.find((option) => option.value == value)?.text)
                        .join(', ');
                    },
                  }}
                >
                  {committees.map((option: StaticSelectOption) => (
                    <MenuItem value={option.value} key={`committee-${option.value}`}>
                      <Checkbox checked={value.indexOf(option.value) >= 0} />
                      <ListItemText primary={option.text} />
                    </MenuItem>
                  ))}
                </TextField>
              )}
              name={'committees'}
              control={control}
              rules={{ required: getRequiredValidationRule('committees') }}
            />
          </GridItem>
          <GridItem>
            <Controller
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  {...defaultInputProps}
                  {...getValidationErrorProps(error)}
                  inputRef={field.ref}
                  label={'Room Name'}
                  required
                />
              )}
              name={'title'}
              control={control}
              rules={{ required: getRequiredValidationRule('title') }}
            />
          </GridItem>
          <GridItem>
            <Controller
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  {...defaultInputProps}
                  {...getValidationErrorProps(error)}
                  inputRef={field.ref}
                  label={'Region'}
                  required
                  select
                >
                  {regions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.text}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              name={'region'}
              control={control}
              rules={{ required: getRequiredValidationRule('region') }}
            />
          </GridItem>
          <GridItem md={6}>
            <MeetingDateTimeFormItem fieldName={'startDate'} label={'Start Date'} required />
          </GridItem>
          <GridItem md={6}>
            <MeetingDateTimeFormItem fieldName={'endDate'} label={'End Date'} required />
          </GridItem>
          <GridItem>
            <Controller
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  {...defaultInputProps}
                  {...getValidationErrorProps(error)}
                  inputRef={field.ref}
                  label={'Time Zone'}
                  required
                />
              )}
              name={'timezone'}
              control={control}
              rules={{ required: getRequiredValidationRule('time zone') }}
            />
          </GridItem>
          {data && (
            <GridItem>
              <Typography variant="subtitle2" color="textSecondary">
                {'Note: Leave password fields blank to keep the current password.'}
              </Typography>
            </GridItem>
          )}
          <GridItem md={6}>
            <PasswordFormItem fieldName={'password'} label={'Password'} required={!data} />
          </GridItem>
          <GridItem md={6}>
            <PasswordFormItem
              fieldName={'confirmPassword'}
              label={'Confirm Password'}
              required={!data}
              validate={validatePassword}
            />
          </GridItem>
        </GridContainer>
      </FormProvider>
    </Modal>
  );
};

export default EditMeetingModal;
